// @flow

import { all, call, put, takeEvery, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import { toast } from "react-toastify";
import config from "@config";
import http from "@http";
import { transformFilters, OPERATORS } from "@transformers/request";
import {
  GetNotifications,
  GetNotificationsSummary,
  CreateNotification,
  UpdateNotification,
  DeleteNotificationList,
  GetHierarchyNotifications,
  GetNotificationsTargets,
  GetNotificationsTargetsSummary,
  CreateNotificationTarget,
  UpdateNotificationTarget,
  DeleteNotificationTargetList,
  GetHierarchyNotificationsTargets,
  GetNotificationSchema,
  GetAddNotificationModalEmployeeList,
  GetNotificationEmployee,
  TransferNotificationList,
  TransferNotificationTargetList,
  CopyNotificationList,
  CopyNotificationTargetList,
  CreateManagerNotification,
  DeleteManagerNotificationList,
  UpdateManagerNotification,
  GetMyEmployeeNotifications,
  GetIndividualsRevisionHistory,
} from "@modules/shared/constants/actions";
import actionCreators from "@modules/shared/actionCreator";
import {
  selectNotificationsIndividualsPage,
  selectNotificationsDepartmentsPage,
} from "@modules/shared/selectors/employee-notifications";
import { EMPLOYEE_REF_TYPES } from "@modules/shared/constants/notifications";
import type {
  EmployeeRefType,
  EmployeeNotification,
} from "@modules/shared/reducer/employee-notifications/types";
import type { EmployeePosition } from "@modules/shared/reducer/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type GetNotificationsRequest = $Exact<{
  clientId: string,
  employeeId: string,
  page?: number,
}>;

export function* onGetNotifications({
  payload,
}: Action<GetNotificationsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error("Invalid payload provided");
  }

  const currentPage = yield select(
    selectNotificationsIndividualsPage,
    EMPLOYEE_REF_TYPES.RECIPIENT,
  );
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/recipient/individual`,
      {
        params: {
          search: transformFilters({ targetEmployeeIsActive: true }),
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    const sortedContent = response.data.content.sort((a, b) => {
      if (a.recipient.firstName < b.recipient.firstName) return -1;
      if (a.recipient.firstName > b.recipient.firstName) return 1;
      return 0;
    });
    const data = {
      ...response.data,
      content: sortedContent,
    };

    yield put(actionCreators.GetNotificationsSuccess(data));
  } catch (e) {
    yield put(actionCreators.GetNotificationsError(e.response));
  }
}

export function* onGetMyEmployeeNotifications({
  payload,
}: Action<GetNotificationsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error("Invalid payload provided");
  }

  const currentPage = yield select(
    selectNotificationsIndividualsPage,
    EMPLOYEE_REF_TYPES.RECIPIENT,
  );
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/my-details/client/${payload.clientId}/notification-contact/my-employee/${payload.employeeId}/additional-individual-notification-recipients`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetMyEmployeeNotificationsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetMyEmployeeNotificationsError(e.response));
  }
}

export function* onGetNotificationsTargets({
  payload,
}: Action<GetNotificationsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error("Invalid payload provided");
  }

  const currentPage = yield select(selectNotificationsIndividualsPage, EMPLOYEE_REF_TYPES.TARGET);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/target/individual`,
      {
        params: {
          search: transformFilters({ recipientEmployeeIsActive: true }),
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetNotificationsTargetsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetNotificationsTargetsError(e.response));
  }
}

export function* onGetHierarchyNotifications({
  payload,
}: Action<GetNotificationsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error("Invalid payload provided");
  }

  const currentPage = yield select(
    selectNotificationsDepartmentsPage,
    EMPLOYEE_REF_TYPES.RECIPIENT,
  );
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/recipient/hierarchy`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetHierarchyNotificationsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetHierarchyNotificationsError(e.response));
  }
}

export function* onGetHierarchyNotificationsTargets({
  payload,
}: Action<GetNotificationsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error("Invalid payload provided");
  }

  const currentPage = yield select(selectNotificationsDepartmentsPage, EMPLOYEE_REF_TYPES.TARGET);
  const page = (payload && payload.page) || currentPage;

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/target/hierarchy`,
      {
        params: {
          page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetHierarchyNotificationsTargetsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetHierarchyNotificationsTargetsError(e.response));
  }
}

export function* onGetNotificationsSummary({
  payload,
}: Action<GetNotificationsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/recipient/summary`,
    );

    yield put(actionCreators.GetNotificationsSummarySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetNotificationsSummaryError(e.response));
  }
}

export function* onGetNotificationsTargetsSummary({
  payload,
}: Action<GetNotificationsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/target/summary`,
    );

    yield put(actionCreators.GetNotificationsTargetsSummarySuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetNotificationsTargetsSummaryError(e.response));
  }
}

type NotificationEmployeesRequest = $Exact<{
  clientId: number,
  page?: number,
  filters?: *,
}>;

export function* onGetAddNotificationModalEmployeeList({
  payload,
}: Action<NotificationEmployeesRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error("Invalid payload provided");
  }
  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee`,
      {
        params: {
          search: transformFilters(payload.filters || {}, {
            last_name: OPERATORS.LIKE,
            employee_ref_primary: OPERATORS.LIKE,
            date_of_birth: OPERATORS.EQUALS,
          }),
          page: payload.page,
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetAddNotificationModalEmployeeListSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAddNotificationModalEmployeeListError(e.response));
  }
}

type NotificationEmployeeRequest = $Exact<{
  clientId: string,
  employeeId: string,
  notificationType: string,
  notificationId: number,
  employeeRefType: EmployeeRefType,
  employeePositions: EmployeePosition[],
  notificationDates: $Exact<{
    activeFrom: ?string,
    activeTo: ?string,
  }>,
}>;

export function* onGetNotificationEmployee({
  payload,
}: Action<NotificationEmployeeRequest>): Generator<*, void, *> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.notificationType ||
    !payload.notificationId ||
    !payload.employeePositions ||
    !payload.notificationDates
  ) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}`,
    );

    yield put(
      actionCreators.ToggleEditNotificationModal({
        visible: true,
        data: {
          employeeDetails: response.data,
          notificationType: payload.notificationType,
          notificationId: payload.notificationId,
          employeeRefType: payload.employeeRefType,
          employeePositions: payload.employeePositions,
          notificationDates: {
            activeFrom: payload.notificationDates.activeFrom,
            activeTo: payload.notificationDates.activeTo,
          },
        },
      }),
    );
  } catch (e) {
    yield put(actionCreators.GetNotificationEmployeeError(e.response));
  }
}

export function* onGetNotificationSchema(): Generator<*, void, *> {
  try {
    const data = yield parseSchema("IndividualNotificationDTO");

    yield put(actionCreators.GetNotificationSchemaSuccess(data));
  } catch (e) {
    yield put(actionCreators.GetNotificationSchemaError(e.response));
  }
}

type CreateNotificationRequest = $Exact<{
  ...GetNotificationsRequest,
  notification: *,
  refreshAction?: () => void,
}>;

export function* onCreateNotification({
  payload,
}: Action<CreateNotificationRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.notification) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/recipient/individual`,
      {
        ...payload.notification,
        recipient: {
          id: payload.notification.recipient.id,
        },
      },
    );

    const successActions = [
      put(actionCreators.CreateNotificationSuccess(response.data)),
      put(actionCreators.ToggleAddNotificationModal(false)),
      put(toast.success("Notification contact added successfully.")),
      put(
        actionCreators.GetNotifications({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ];

    if (payload.refreshAction) {
      successActions.push(put(payload.refreshAction()));
    }

    yield all(successActions);
  } catch (e) {
    yield put(actionCreators.CreateNotificationError(e.response));
  }
}

export function* onCreateNotificationTarget({
  payload,
}: Action<CreateNotificationRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.notification) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.notification.recipient.id}/notification/recipient/individual`,
      {
        ...payload.notification,
        recipient: {
          id: payload.employeeId,
        },
      },
    );

    const successActions = [
      put(actionCreators.CreateNotificationTargetSuccess(response.data)),
      put(actionCreators.ToggleAddNotificationModal(false)),
      put(toast.success("Employee added successfully.")),
      put(
        actionCreators.GetNotificationsTargets({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsTargetsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ];

    if (payload.refreshAction) {
      successActions.push(put(payload.refreshAction()));
    }

    yield all(successActions);
  } catch (e) {
    yield put(actionCreators.CreateNotificationTargetError(e.response));
  }
}

export function* onUpdateNotification({
  payload,
}: Action<CreateNotificationRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.notification) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/recipient/individual/${payload.notification.id}`,
      {
        id: payload.notification.id,
        ...payload.notification,
        recipient: {
          id: payload.notification.recipient.id,
        },
      },
    );

    yield all([
      put(actionCreators.UpdateNotificationSuccess(response.data)),
      put(actionCreators.ToggleEditNotificationModal(false)),
      put(toast.success("Notification contact updated successfully.")),
      put(
        actionCreators.GetNotifications({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateNotificationError(e.response));
  }
}

export function* onUpdateNotificationTarget({
  payload,
}: Action<CreateNotificationRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.notification) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.notification.recipient.id}/notification/recipient/individual/${payload.notification.id}`,
      {
        id: payload.notification.id,
        ...payload.notification,
        recipient: {
          id: payload.employeeId,
        },
      },
    );

    yield all([
      put(actionCreators.UpdateNotificationTargetSuccess(response.data)),
      put(actionCreators.ToggleEditNotificationModal(false)),
      put(toast.success("Notification contact updated successfully.")),
      put(
        actionCreators.GetNotificationsTargets({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsTargetsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateNotificationTargetError(e.response));
  }
}

type DeleteNotificationListRequest = $Exact<{
  ...GetNotificationsRequest,
  notificationIdList: Array<number>,
}>;

export function* onDeleteNotificationList({
  payload,
}: Action<DeleteNotificationListRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.notificationIdList) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.delete,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/recipient/individuals`,
      {
        data: {
          ids: payload.notificationIdList,
        },
      },
    );

    yield all([
      put(actionCreators.DeleteNotificationListSuccess(response.data)),
      put(toast.success("Notification contact removed successfully.")),
      put(
        actionCreators.GetNotifications({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteNotificationListError(e.response));
  }
}

export function* onDeleteNotificationTargetList({
  payload,
}: Action<DeleteNotificationListRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.notificationIdList) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.delete,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/target/individuals`,
      {
        data: {
          ids: payload.notificationIdList,
        },
      },
    );

    yield all([
      put(actionCreators.DeleteNotificationTargetListSuccess(response.data)),
      put(toast.success("Notification contact removed successfully.")),
      put(
        actionCreators.GetNotificationsTargets({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsTargetsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteNotificationTargetListError(e.response));
  }
}

type TransferNotificationListRequest = $Exact<{
  ...GetNotificationsRequest,
  notificationList: Array<EmployeeNotification>,
  transferTargetId: number,
  successMessage: string,
}>;

export function* onTransferNotificationList({
  payload,
}: Action<TransferNotificationListRequest>): Generator<*, void, void> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.notificationList ||
    !payload.transferTargetId
  ) {
    throw Error("Invalid payload provided");
  }

  try {
    yield call(
      http.delete,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/recipient/individuals`,
      {
        data: {
          ids: payload.notificationList.map(notification => notification.id),
        },
      },
    );

    yield all(
      payload.notificationList.map(notification =>
        call(
          http.post,
          `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.transferTargetId}/notification/recipient/individual`,
          {
            ...notification,
            id: null,
          },
        ),
      ),
    );

    yield all([
      put(actionCreators.TransferNotificationListSuccess()),
      put(
        actionCreators.GetNotifications({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.TransferNotificationListError(e.response));
  }
}

export function* onTransferNotificationTargetList({
  payload,
}: Action<TransferNotificationListRequest>): Generator<*, void, void> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.notificationList ||
    !payload.transferTargetId
  ) {
    throw Error("Invalid payload provided");
  }

  try {
    yield call(
      http.delete,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/notification/target/individuals`,
      {
        data: {
          ids: payload.notificationList.map(notification => notification.id),
        },
      },
    );

    yield all(
      payload.notificationList.map(notification =>
        call(
          http.post,
          `/api/v1/employee-admin/client/${payload.clientId}/employee/${notification.target.id}/notification/recipient/individual`,
          {
            ...notification,
            recipient: {
              id: payload.transferTargetId,
            },
            id: null,
          },
        ),
      ),
    );

    yield all([
      put(actionCreators.TransferNotificationTargetListSuccess()),
      put(
        actionCreators.GetNotificationsTargets({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsTargetsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.TransferNotificationTargetListError(e.response));
  }
}

export function* onCopyNotificationList({
  payload,
}: Action<TransferNotificationListRequest>): Generator<*, void, void> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.notificationList ||
    !payload.transferTargetId
  ) {
    throw Error("Invalid payload provided");
  }

  try {
    yield all(
      payload.notificationList.map(notification =>
        call(
          http.post,
          `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.transferTargetId}/notification/recipient/individual`,
          {
            ...notification,
            id: null,
          },
        ),
      ),
    );

    yield all([
      put(actionCreators.CopyNotificationListSuccess()),
      put(
        actionCreators.GetNotifications({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.CopyNotificationListError(e.response));
  }
}

export function* onCopyNotificationTargetList({
  payload,
}: Action<TransferNotificationListRequest>): Generator<*, void, void> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.employeeId ||
    !payload.notificationList ||
    !payload.transferTargetId
  ) {
    throw Error("Invalid payload provided");
  }

  try {
    yield all(
      payload.notificationList.map(notification =>
        call(
          http.post,
          `/api/v1/employee-admin/client/${payload.clientId}/employee/${notification.target.id}/notification/recipient/individual`,
          {
            ...notification,
            recipient: {
              id: payload.transferTargetId,
            },
            id: null,
          },
        ),
      ),
    );

    yield all([
      put(actionCreators.CopyNotificationTargetListSuccess()),
      put(
        actionCreators.GetNotificationsTargets({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsTargetsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.CopyNotificationTargetListError(e.response));
  }
}
export function* onCreateManagerNotification({
  payload,
}: Action<CreateNotificationRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId || !payload.notification) {
      throw Error("Invalid payload provided");
    }
    const response = yield call(
      http.post,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager/notification/recipient/individual`,
      {
        ...payload.notification,
        recipient: {
          id: payload.notification.recipient.id,
        },
      },
    );

    const successActions = [
      put(actionCreators.CreateNotificationSuccess(response.data)),
      put(actionCreators.ToggleAddNotificationModal(false)),
      put(toast.success("Notification contact added successfully.")),
      put(
        actionCreators.GetNotifications({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ];

    if (payload.refreshAction) {
      successActions.push(put(payload.refreshAction()));
    }

    yield all(successActions);
  } catch (e) {
    yield put(actionCreators.CreateNotificationError(e.response));
  }
}

export function* onDeleteManagerNotificationList({
  payload,
}: Action<DeleteNotificationListRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.notificationIdList) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.delete,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager/notification/recipient/individuals`,
      {
        data: {
          ids: payload.notificationIdList,
        },
      },
    );

    yield all([
      put(actionCreators.DeleteNotificationListSuccess(response.data)),
      put(toast.success("Notification contact removed successfully.")),
      put(
        actionCreators.GetNotifications({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteNotificationListError(e.response));
  }
}

export function* onUpdateManagerNotification({
  payload,
}: Action<CreateNotificationRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.employeeId || !payload.notification) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/manager/notification/recipient/individual/${payload.notification.id}`,
      {
        id: payload.notification.id,
        ...payload.notification,
        recipient: {
          id: payload.notification.recipient.id,
        },
      },
    );

    yield all([
      put(actionCreators.UpdateNotificationSuccess(response.data)),
      put(actionCreators.ToggleEditNotificationModal(false)),
      put(toast.success("Notification contact updated successfully.")),
      put(
        actionCreators.GetNotifications({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
      put(
        actionCreators.GetNotificationsSummary({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateNotificationError(e.response));
  }
}

type NotificationsHistoryRequest = $Exact<{
  clientId: string,
  notificationId: string,
}>;

export function* onGetIndividualsRevisionHistory({
  payload,
}: Action<NotificationsHistoryRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.notificationId) {
    throw Error("Invalid payload provided");
  }

  try {
    const response = yield call(
      http.get,
      `api/v1/employee-admin/client/${payload.clientId}/notification/history/${payload.notificationId}`,
    );

    yield all([
      put(actionCreators.GetIndividualsRevisionHistorySuccess(response.data)),
      put(actionCreators.ToggleIndividualsRevisionHistoryModal()),
    ]);
  } catch (e) {
    yield put(actionCreators.GetIndividualsRevisionHistoryError(e.response));
  }
}

export function* NotificationsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetNotifications, onGetNotifications),
    takeEvery(GetNotificationsSummary, onGetNotificationsSummary),
    takeEvery(CreateNotification, onCreateNotification),
    takeEvery(UpdateNotification, onUpdateNotification),
    takeEvery(DeleteNotificationList, onDeleteNotificationList),
    takeEvery(GetNotificationsTargets, onGetNotificationsTargets),
    takeEvery(GetNotificationsTargetsSummary, onGetNotificationsTargetsSummary),
    takeEvery(CreateNotificationTarget, onCreateNotificationTarget),
    takeEvery(UpdateNotificationTarget, onUpdateNotificationTarget),
    takeEvery(DeleteNotificationTargetList, onDeleteNotificationTargetList),
    takeEvery(GetNotificationSchema, onGetNotificationSchema),
    takeEvery(GetAddNotificationModalEmployeeList, onGetAddNotificationModalEmployeeList),
    takeEvery(GetNotificationEmployee, onGetNotificationEmployee),
    takeEvery(GetHierarchyNotifications, onGetHierarchyNotifications),
    takeEvery(GetHierarchyNotificationsTargets, onGetHierarchyNotificationsTargets),
    takeEvery(TransferNotificationList, onTransferNotificationList),
    takeEvery(TransferNotificationTargetList, onTransferNotificationTargetList),
    takeEvery(CopyNotificationList, onCopyNotificationList),
    takeEvery(CopyNotificationTargetList, onCopyNotificationTargetList),
    takeEvery(CreateManagerNotification, onCreateManagerNotification),
    takeEvery(DeleteManagerNotificationList, onDeleteManagerNotificationList),
    takeEvery(UpdateManagerNotification, onUpdateManagerNotification),
    takeEvery(GetMyEmployeeNotifications, onGetMyEmployeeNotifications),
    takeEvery(GetIndividualsRevisionHistory, onGetIndividualsRevisionHistory),
  ]);
}

export default NotificationsSagas;
